import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { useI18n, type Dictionary } from "../../i18n";

type WorkspaceErrorBoundaryProps = {
  children: ReactNode;
};

type WorkspaceErrorBoundaryInnerProps = WorkspaceErrorBoundaryProps & {
  t: Dictionary;
};

type WorkspaceErrorBoundaryState = {
  error: Error | null;
};

export function WorkspaceErrorBoundary({ children }: WorkspaceErrorBoundaryProps) {
  const { t } = useI18n();
  return <WorkspaceErrorBoundaryInner t={t}>{children}</WorkspaceErrorBoundaryInner>;
}

class WorkspaceErrorBoundaryInner extends Component<
  WorkspaceErrorBoundaryInnerProps,
  WorkspaceErrorBoundaryState
> {
  state: WorkspaceErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): WorkspaceErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Salesko workspace render failed", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <section className="workspace-error-boundary" role="alert" aria-labelledby="workspace-error-title">
          <span className="workspace-error-mark" aria-hidden="true">
            <AlertTriangle size={28} />
          </span>
          <div>
            <p className="eyebrow">{this.props.t.workspaceError.eyebrow}</p>
            <h1 id="workspace-error-title">{this.props.t.workspaceError.title}</h1>
            <p>{this.state.error.message || this.props.t.workspaceError.fallback}</p>
          </div>
          <button className="primary-button" type="button" onClick={this.handleRetry}>
            <RotateCcw size={17} />
            <span>{this.props.t.workspaceError.retry}</span>
          </button>
        </section>
      );
    }

    return this.props.children;
  }
}
